import React, { useEffect, useState, useCallback } from "react";
import NavigateBack from "../../components/navigateBack";
import { Container, Grid, makeStyles, Typography } from "@material-ui/core";
import BasdaiAssessment from "./basdaiAssessment";
import createPersistedState from "use-persisted-state";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import BasfiAssessment from "./basfiAssessment";

const useTokenState = createPersistedState("token");
const usePIDState = createPersistedState("pid");

const TAB_INDEX_MAP = {
  BASDAI: 0,
  BASFI: 1,
};

const AnkylosingSpondylitisAssessment = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentAppointment, setCurrentAppointment] = useState(null);
  const [currentBasdaiInfo, setCurrentBasdaiInfo] = useState(null);
  const [currentBasfiInfo, setCurrentBasfiInfo] = useState(null);
  const [tab, setTab] = useState(TAB_INDEX_MAP.BASDAI);
  const [error, setError] = useState(null);

  const history = useHistory();
  const [token] = useTokenState();
  const [pid] = usePIDState();
  const location = useLocation();

  const useStyles = makeStyles((theme) => ({
    selectedTab: {
      backgroundColor: "#0e426a",
      height: 50,
      color: "#ffffff",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      cursor: "pointer",
    },
    unselectedTab: {
      backgroundColor: "#97d1fa57",
      height: 50,
      color: "#0060a6b3",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      cursor: "pointer",
    },
    disabled: {
      pointerEvents: "none",
      opacity: 0.5,
    },
    errorMessage: {
      color: "#d32f2f",
      textAlign: "center",
      padding: theme.spacing(2),
      marginTop: "78px",
    },
  }));
  const classes = useStyles();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const assessment = queryParams.get("assessment");

    const index = TAB_INDEX_MAP[assessment];
    if (typeof index === "number") {
      setTab(index);
    }
  }, [location.search]);

  // Centralized error and auth handling
  const handleApiError = useCallback(
    (error, responseData) => {
      if (responseData?.status?.code === 4011) {
        localStorage.clear();
        history.push("/login");
        return;
      }

      console.error("API Error:", error);
      setError(error.message || "An error occurred");
    },
    [history]
  );

  // Fetch BASDAI score and details
  const fetchBasdaiPainScore = useCallback(async () => {
    if (!pid || !token) return null;

    try {
      const response = await fetch(`/api/v1/pain-score/basdai/${pid}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = await response.json();

      if (responseData?.status?.success) {
        setCurrentBasdaiInfo(responseData.current);
        return responseData.current;
      } else {
        handleApiError(new Error("Failed to fetch BASDAI score"), responseData);
        return null;
      }
    } catch (error) {
      handleApiError(error);
      return null;
    }
  }, [pid, token, handleApiError]);

  // Fetch BASDAI score and details
  const fetchBasfiPainScore = useCallback(async () => {
    if (!pid || !token) return null;

    try {
      const response = await fetch(`/api/v1/pain-score/basfi/${pid}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = await response.json();

      if (responseData?.status?.success) {
        setCurrentBasfiInfo(responseData.current);
        return responseData.current;
      } else {
        handleApiError(new Error("Failed to fetch BASFI score"), responseData);
        return null;
      }
    } catch (error) {
      handleApiError(error);
      return null;
    }
  }, [pid, token, handleApiError]);

  // Appointment fetching
  const fetchAppointmentInfo = useCallback(async () => {
    if (!pid || !token) return null;

    try {
      const response = await fetch(`/api/v1/appointments?attendeeId=${pid}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = await response.json();

      if (responseData?.status?.success) {
        setCurrentAppointment(responseData.appointment);
        return responseData.appointment;
      } else {
        handleApiError(new Error("Failed to fetch appointment"), responseData);
        return null;
      }
    } catch (error) {
      handleApiError(error);
      return null;
    }
  }, [pid, token, handleApiError]);

  // Initialize data on mount
  useEffect(() => {
    const initializeData = async () => {
      if (!pid || !token) {
        history.push("/login");
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        await Promise.all([
          fetchAppointmentInfo(),
          fetchBasdaiPainScore(),
          fetchBasfiPainScore(),
        ]);
      } catch (error) {
        setError("Failed to load initial data");
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, [pid, token, fetchAppointmentInfo, fetchBasdaiPainScore, history]);

  // Callback to update BASDAI info from child component
  const handleBasdaiInfoUpdate = useCallback((newBasdaiInfo) => {
    setCurrentBasdaiInfo(newBasdaiInfo);
  }, []);

  // Callback to update BASFI info from child component
  const handleBasfiInfoUpdate = useCallback((newBasfiInfo) => {
    setCurrentBasfiInfo(newBasfiInfo);
  }, []);

  if (isLoading) {
    return (
      <>
        <NavigateBack title="Ankylosing Spondylitis" />
        <div className="body_content">
          <div className="inner_body py-5">
            <div
              style={{
                margin: "auto",
                display: "flex",
                justifyContent: "center",
              }}
              id="loader"
            />
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <NavigateBack title="Ankylosing Spondylitis" />
        <div className={classes.errorMessage}>
          <Typography variant="h6">Error: {error}</Typography>
          <button
            onClick={() => window.location.reload()}
            className="btn btn_primary mt-3"
          >
            Retry
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <NavigateBack title="Ankylosing Spondylitis" />
      <div style={{ marginTop: "75px" }}>
        <Grid container spacing={0}>
          {/* Tab Navigation */}
          <Grid item xs={6}>
            <Container
              maxWidth="sm"
              disableGutters
              onClick={() => setTab(TAB_INDEX_MAP.BASDAI)}
            >
              <Typography
                component="div"
                className={
                  tab === TAB_INDEX_MAP.BASDAI
                    ? classes.selectedTab
                    : classes.unselectedTab
                }
              >
                BASDAI
              </Typography>
            </Container>
          </Grid>
          <Grid item xs={6}>
            <Container
              maxWidth="sm"
              disableGutters
              // className={classes.disabled}
              onClick={() => setTab(TAB_INDEX_MAP.BASFI)}
            >
              <Typography
                component="div"
                className={
                  tab === TAB_INDEX_MAP.BASFI
                    ? classes.selectedTab
                    : classes.unselectedTab
                }
              >
                BASFI
              </Typography>
            </Container>
          </Grid>

          {/* Tab Content */}
          {tab === TAB_INDEX_MAP.BASDAI && currentAppointment && (
            <Grid item xs={12}>
              <Container maxWidth="sm" disableGutters>
                <BasdaiAssessment
                  appointmentId={currentAppointment.id}
                  pid={pid}
                  token={token}
                  currentBasdaiInfo={currentBasdaiInfo}
                  onBasdaiInfoUpdate={handleBasdaiInfoUpdate}
                  onError={handleApiError}
                />
              </Container>
            </Grid>
          )}

          {tab === TAB_INDEX_MAP.BASFI && (
            <Grid item xs={12}>
              <Container maxWidth="sm" disableGutters>
                <BasfiAssessment
                  appointmentId={currentAppointment.id}
                  pid={pid}
                  token={token}
                  currentBasfiInfo={currentBasfiInfo}
                  onBasfiInfoUpdate={handleBasfiInfoUpdate}
                  onError={handleApiError}
                />
              </Container>
            </Grid>
          )}
        </Grid>
      </div>
    </>
  );
};

export default AnkylosingSpondylitisAssessment;
